import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiDateFilterBar, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface CustomerRow {
  id: number
  name: string
  amount: number
  percent: number
  top_by_dollars: boolean
  top_by_count: boolean
}

interface Props {
  customers: CustomerRow[]
  start_date: string
  end_date: string
  report_total: number
}

/* -- Helpers ---------------------------------------------- */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatDate(dateStr: string): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function rowStyle(row: CustomerRow): React.CSSProperties | undefined {
  if (row.top_by_dollars) return { backgroundColor: "#FFFFCC" }
  return undefined
}

function rowTextClass(row: CustomerRow): string {
  return row.top_by_count ? "font-bold" : ""
}

/* -- Columns ---------------------------------------------- */

const PER_PAGE = 50

const columns = (
  startDate: string,
  endDate: string,
): HpiColumn<CustomerRow>[] => [
  {
    header: "Client",
    render: (row) => (
      <div
        style={rowStyle(row)}
        className={`-mx-4 -my-3 px-4 py-3 ${rowTextClass(row)}`}
      >
        <Link
          href={`/customers/${row.id}/reports/referrals?start_date=${startDate}&end_date=${endDate}`}
          className="text-sm text-blue-600 hover:underline"
          target="_blank"
        >
          {row.name}
        </Link>
      </div>
    ),
  },
  {
    header: "Amount",
    width: "max-w-[140px]",
    align: "right",
    render: (row) => (
      <div
        style={rowStyle(row)}
        className={`-mx-4 -my-3 px-4 py-3 text-right text-sm ${rowTextClass(row)}`}
      >
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  {
    header: "% of Total",
    width: "max-w-[100px]",
    align: "right",
    render: (row) => (
      <div
        style={rowStyle(row)}
        className={`-mx-4 -my-3 px-4 py-3 text-right text-sm ${rowTextClass(row)}`}
      >
        {row.percent.toFixed(2)}%
      </div>
    ),
  },
]

/* -- Main Component --------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/eight_twenty" },
  { title: "80/20 Report", href: "/reports/eight_twenty" },
]

export default function EightTwentyReport({
  customers,
  start_date,
  end_date,
  report_total,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(customers.length / PER_PAGE)
  const paginated = customers.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/eight_twenty",
      {
        start_date: startDate,
        end_date: endDate,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="80/20 Report" />

      <div className="p-6">
        <div className="mb-4 text-center">
          <p className="text-muted-foreground text-sm">
            <span
              className="mr-4 inline-block rounded px-2 py-0.5"
              style={{ backgroundColor: "#FFFFCC", border: "1px solid #999" }}
            >
              &nbsp;&nbsp;&nbsp;
            </span>
            Top 20% by $s
            <span className="ml-6 font-bold">Bold</span> = Top 20% by # of
            Customers who referred cases
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            Date is when case was opened, showing top 20% by # of clients, not
            showing individuals w/ no referral entered
          </p>
        </div>

        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        <HpiDataTable<CustomerRow>
          title={`${formatDate(start_date)} to ${formatDate(end_date)} (${formatCurrency(report_total)})`}
          columns={columns(startDate, endDate)}
          data={paginated}
          keyFn={(row) => row.id}
          emptyMessage="No cases found in date range."
          pagination={
            customers.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={customers.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />
      </div>
    </AppLayout>
  )
}
